import type { PhaseSiteDto, PhaseTwoDto, WizardStepDto } from '@bk/contracts';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant } from '../../lib/useTenant';
import { Figures, talkAbout } from './Fase';
import { PhaseFrame, usePhase } from './fasi-comune';

/**
 * La fase 2 «Le sedi, con gli attributi» (docs/mockup/percorso-sinapsi/05-fase2; contratto n. 1 versione 2.0, art. 8,
 * fase 2; ADR 0174): i bloccanti, i numeri, ogni sede con tipo, uso, indirizzo, addetti, attività, insegna e la tabella
 * degli attributi in tre pesi con la fonte o l'esito; le altre sedi online; aggiungere, correggere o togliere una sede.
 */
export function Fase2Page({ next, onNext, sealing, stopped = false }: { next: WizardStepDto | null; onNext: () => void; sealing: boolean; stopped?: boolean }) {
  const { q, reload, confirm, redo } = usePhase<PhaseTwoDto>(2);
  return (
    <PhaseFrame
      d={q.data}
      error={q.error}
      eyebrow="Fase 2 · Le sedi"
      title={(d) => `Dove lavora ${d.azienda ?? 'la tua azienda'}, com’è fatto ogni luogo`}
      working="Preparo le sedi"
      questionsIntro="Solo gli attributi che cambiano un obbligo e che nessuna fonte dà. Niente di ciò che ti ho già chiesto nella fase 0."
      scrapIntro="Per ogni sede, ciò che l’azienda al suo meglio avrebbe e che le fonti oggi non mostrano."
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
    >
      {(d) => (
        <>
          <Figures list={d.numeri} />
          {d.sedi.map((s) => (
            <Site key={s.id} s={s} onChange={reload} />
          ))}
          <OtherSites d={d} onChange={reload} />
        </>
      )}
    </PhaseFrame>
  );
}

const weightOf = (p: string) => (p === 'a' ? '' : p);
const valueTone = (e: string) => (e === 'da_confermare' ? 'gold' : e === 'non_trovato' || e === 'non_si_applica' ? 'mute' : '');
const sourceTone = (f: string, g: string | null) => (g === 'T' || /· T$/.test(f) ? 'T' : g === 'A' || / · A$/.test(f) ? 'on' : '');

function Site({ s, onChange }: { s: PhaseSiteDto; onChange: () => void }) {
  const { t, isManager } = useTenant();
  const [why, setWhy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const doubt = s.attributi.some((a) => a.peso === 'a' && a.esito === 'da_confermare') || /scelta di Timonio/.test(s.addetti_fonte);
  const remove = async (motivo: 'non_esiste_piu' | 'non_nostra') => {
    setErr(null);
    try {
      await api(t(`/sites/${s.id}`), { method: 'PATCH', body: { status: 'chiusa', motivo } });
      onChange();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <article className={`sn-site ${doubt ? 'gold' : ''}`}>
      <div className="sn-ph">
        <h2 className="sn-h2">{s.nome}</h2>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {s.tipo && <span className={`sn-tag ${s.tipo === 'interna' ? '' : 'ok'}`}>{s.tipo}</span>}
          {s.uso && <span className="sn-tag">Uso: {s.uso}</span>}
          {s.grado && <span className={`sn-tag ${s.grado === 'certo' ? 'ok' : 'gold'}`}>{s.grado}</span>}
          {s.del_titolare && <span className="sn-tag ok">tua</span>}
        </span>
      </div>
      <p className="line">
        {s.indirizzo ?? <span className="sn-gold">indirizzo da confermare</span>} <span className={`sn-src ${/T$/.test(s.indirizzo_fonte) ? 'violet' : /confermare/.test(s.indirizzo_fonte) ? 'gold' : ''}`}>· {s.indirizzo_fonte}</span> · {s.addetti ?? '?'} {s.addetti === 1 ? 'addetto' : 'addetti'}{' '}
        <span className={`sn-src ${/scelta/.test(s.addetti_fonte) ? 'gold' : /T$/.test(s.addetti_fonte) ? 'violet' : ''}`}>· {s.addetti_fonte}</span>
        {s.attivita && (
          <>
            {' '}
            · attività: {s.attivita} {s.attivita_fonte && <span className="sn-src">· {s.attivita_fonte}</span>}
          </>
        )}{' '}
        · insegna: {s.insegna}
      </p>
      {s.perche && <p className="sn-note">{s.perche}</p>}
      {s.nota && <p className="sn-note">{s.nota}</p>}
      <table className="sn-tbl">
        <thead>
          <tr>
            <th style={{ width: 40 }}>Peso</th>
            <th style={{ width: 170 }}>Gruppo</th>
            <th>Cosa risulta</th>
            <th style={{ width: 200 }}>Fonte</th>
          </tr>
        </thead>
        <tbody>
          {s.attributi.map((a) => (
            <tr key={a.gruppo}>
              <td className={`w ${weightOf(a.peso)}`}>({a.peso})</td>
              <td className="g">{a.gruppo}</td>
              <td className={`v ${valueTone(a.esito)}`}>{a.valore}</td>
              <td className={`f ${sourceTone(a.fonte, a.grado)}`}>{a.fonte}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="acts">
        <button type="button" className="sn-link" style={{ fontSize: 14, fontWeight: 600 }} onClick={() => setWhy(!why)}>
          Perché? Tutte le fonti di questa sede
        </button>
        {isManager && (
          <>
            <button type="button" className="sn-link" style={{ color: '#c3cbe0' }} onClick={() => talkAbout(`Sulla sede ${s.nome}: `)}>
              Correggi
            </button>
            {!removing ? (
              <button type="button" className="sn-link" style={{ color: '#c3cbe0' }} onClick={() => setRemoving(true)}>
                Togli questa sede
              </button>
            ) : (
              <span style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap' }}>
                <span className="sn-note">Perché la togli?</span>
                <button type="button" className="sn-link" onClick={() => void remove('non_esiste_piu')}>
                  Non esiste più
                </button>
                <button type="button" className="sn-link" onClick={() => void remove('non_nostra')}>
                  Non è nostra
                </button>
                <button type="button" className="sn-link" style={{ color: '#8b97b5' }} onClick={() => setRemoving(false)}>
                  Annulla
                </button>
              </span>
            )}
          </>
        )}
      </div>
      {why && (
        <p className="sn-why" style={{ fontSize: 13 }}>
          <span className="q">Le fonti di questa sede.</span> Indirizzo: {s.indirizzo_fonte}. Addetti: {s.addetti_fonte}. {s.attributi.map((a) => `${a.gruppo}: ${a.fonte}`).join('; ')}. Una sede che togli o correggi resta tua: nessuna fase la riscrive.
        </p>
      )}
      {err && <p className="sn-err">{err}</p>}
    </article>
  );
}

function OtherSites({ d, onChange }: { d: PhaseTwoDto; onChange: () => void }) {
  const { t, isManager } = useTenant();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', address: '', municipality: '' });
  const [err, setErr] = useState<string | null>(null);
  const add = async () => {
    setErr(null);
    try {
      await api(t('/sites'), { method: 'POST', body: { name: f.name.trim(), ...(f.address.trim() ? { address: f.address.trim() } : {}), ...(f.municipality.trim() ? { municipality: f.municipality.trim() } : {}) } });
      setAdding(false);
      setF({ name: '', address: '', municipality: '' });
      onChange();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <section className="sn-panel" style={{ gap: 8, padding: '16px 22px' }}>
      <p className="sn-ey">Altre sedi</p>
      <p style={{ fontSize: 14, color: '#c3cbe0' }}>
        {d.altre_sedi || 'Ho cercato nelle pagine che riportano la partita IVA.'}
        {d.addetti_senza_sede.perche ? ` Addetti senza sede: ${d.addetti_senza_sede.numero}, ${d.addetti_senza_sede.perche}` : ''}
      </p>
      {isManager && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
          {!adding ? (
            <button type="button" className="sn-btn ghost sm" style={{ fontSize: 13 }} onClick={() => setAdding(true)}>
              Aggiungi una sede che manca
            </button>
          ) : (
            <form
              className="sn-talk"
              style={{ border: 0, padding: 0, background: 'transparent', width: '100%' }}
              onSubmit={(e) => {
                e.preventDefault();
                if (f.name.trim()) void add();
              }}
            >
              <input className="sn-input" placeholder="Come la chiamate (per esempio: magazzino di Bitonto)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} aria-label="Nome della sede" />
              <input className="sn-input" placeholder="Via e numero" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} aria-label="Indirizzo" />
              <input className="sn-input" placeholder="Comune" value={f.municipality} onChange={(e) => setF({ ...f, municipality: e.target.value })} aria-label="Comune" />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="sn-btn sm" disabled={!f.name.trim()}>
                  Aggiungi
                </button>
                <button type="button" className="sn-btn ghost sm" onClick={() => setAdding(false)}>
                  Annulla
                </button>
              </div>
            </form>
          )}
          <span className="sn-note">Una sede che aggiungi o togli resta tua: nessuna fase la riscrive. Su ogni sede trovi «togli» e «correggi».</span>
          {err && <span className="sn-err">{err}</span>}
        </div>
      )}
    </section>
  );
}
